import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
	Container,
	Row,
	Col,
	Pagination,
	PaginationItem,
	PaginationLink,
	Input,

} from "reactstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../../assets/css/learn.css";
import { BlogData, BlogDetail } from "../../api/blogService";
import { useLocation, useNavigate } from "react-router-dom";
import FullScreenLoader from "../../components/Common/Loader";

const ImageWithSkeleton = ({
	src,
	alt,
	height = 250,
	width = "100%",
	className = "",
}) => {
	const fallbackImg = "/assets/images/noImage/no-image3.png";
	const [loaded, setLoaded] = useState(false);
	return (
		<div
			className="image-skeleton-wrapper"
			style={{
				position: "relative",
				width: typeof width === "number" ? `${width}px` : width,
				height: typeof height === "number" ? `${height}px` : height,
				overflow: "hidden",
				background: "#fff",
			}}
		>
			<Skeleton
				height="100%"
				width="100%"
				baseColor="#e0e0e0"
				highlightColor="#f5f5f5"
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					opacity: loaded ? 0 : 1,
					transition: "opacity 0.4s ease-in-out",
					zIndex: 1,
				}}
			/>
			<img
				src={src}
				alt={alt}
				className={className}
				loading="lazy"
				onError={(e) => {
					e.target.src = fallbackImg;
					setLoaded(true);
				}}
				onLoad={() => setLoaded(true)}
				style={{
					width: "100%",
					height: "100%",
					maxWidth: "350px",
					margin: "0 auto",
					objectFit: "contain",
					position: "relative",
					top: 0,
					left: 0,
					opacity: loaded ? 1 : 0,
					transition: "opacity 0.5s ease-in",
					zIndex: 2,
				}}
			/>
		</div>
	);
};

const BlogsSection = () => {
	const [showLoader, setShowLoader] = useState(false);
	const [blogs, setBlogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [pageNo, setPageNo] = useState(1);
	const [pageSize, setPageSize] = useState(6);
	const [totalPages, setTotalPages] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [selectedBlog, setSelectedBlog] = useState(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [error, setError] = useState("");
	const [sortOrder, setSortOrder] = useState("Latest");
	const fallbackImg = "/assets/images/noImage/no-image3.png";
	const location = useLocation();
	const navigate = useNavigate();
	const [progress, setProgress] = useState(0);
	const [dataReady, setDataReady] = useState(false);

	useEffect(() => {
		if (!showLoader) return;
		let value = 10;
		setProgress(value);
		const interval = setInterval(() => {
			value = Math.min(value + Math.random() * 15, 90);
			setProgress(value);
		}, 400);
		return () => clearInterval(interval);
	}, [showLoader]);

	const fetchBlogs = async (page, keyword = "", sort = "Latest") => {
		setLoading(true);  
		setShowLoader(true);
		setDataReady(false);
		try {
			const escapeXml = (unsafe) =>
				unsafe.replace(/[<>&'"]/g, (c) => {
					switch (c) {
						case "<": return "&lt;";
						case ">": return "&gt;";
						case "&": return "&amp;";
						case "'": return "&apos;";
						case '"': return "&quot;";
					}
				});

			const XmlStr = `<DocumentElement><Table><FILTERTEXT>${keyword ? escapeXml(keyword) : ""}</FILTERTEXT><SORT>${sort}</SORT></Table></DocumentElement>`.trim();

			const params = {
				XmlStr,
				Sort: sort,
				PageNo: page,
				PageSize: pageSize
			};

			const response = await BlogData(params);

			if (response?.Message?.[0]?.RETURNCODE === "0") {
				const msg = response?.Message?.[0]?.RETURNMESSAGE || "Failed to fetch blogs";
				console.error("Error fetching blogs:", msg);
				setError(msg);
				setBlogs([]);
				setTotalPages(1);
				return;
			}

			if (response?.Message?.[0]?.RETURNMESSAGE === "Success") {
				setBlogs(response.Posts || []);
				setTotalPages(Number(response.PagingInformation?.[0]?.TOTAL_PAGE) || 1);
			} else {
				setBlogs([]);
				setTotalPages(1);
			}
		} catch (error) {
			console.error("Error fetching blogs:", error);
			setBlogs([]);
			setTotalPages(1);
		} finally {
			setProgress(100);
			setTimeout(() => {
				setLoading(false);  
				setShowLoader(false);
				setDataReady(true);
				setProgress(0);
			}, 400);
		}
	};
	const handleReadMore = async (slug) => {
		try {
			setDetailLoading(true);
			const data = await BlogDetail(slug);
			setSelectedBlog(data);
		} catch (error) {
			console.error("Failed to load blog detail:", error);
		} finally {
			setDetailLoading(false);
		}
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const categoryFromURL = params.get("category");

		if (categoryFromURL) {
			setSearchTerm(categoryFromURL);
			setSearchInput(categoryFromURL);
			fetchBlogs(pageNo, categoryFromURL);
		} else {
			fetchBlogs(pageNo, searchInput, sortOrder);

		}
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [pageNo, searchTerm, sortOrder, location.search]);

	const handlePageChange = (newPage) => {
		if (newPage > 0 && newPage <= totalPages && newPage !== pageNo) {
			setPageNo(newPage);
			fetchBlogs(pageNo, searchTerm.trim(), sortOrder);
		}
	};

	const handleSearchKeyPress = (e) => {
		if (e.key === "Enter") {
			setSearchTerm(searchInput);
			setPageNo(1);
		}
	};

	const removeParam = () => {
		const params = new URLSearchParams(location.search);
		params.delete('category');

		navigate({
			pathname: location.pathname,
			search: params.toString(),
		});

		if (pageNo > 1) {
			setPageNo(1);
		} else {
			setSearchInput("");
		}
	};

	return (
		<section className="learn-section">
			{showLoader && <FullScreenLoader progress={progress} />}
			<div className="learn-wrapper mx-auto">
				<div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
					<h1 className="learn-header fw-bold text-primary">Learn</h1>

					<div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
						<div className="d-flex align-items-center gap-2 flex-wrap">
							<div style={{ position: "relative" }}>
								<Input
									type="text"
									placeholder="Search blogs..."
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									onKeyDown={handleSearchKeyPress}
									className="form-control rounded-pill shadow-sm px-4"
									style={{
										height: "45px",
										borderColor: "#ced4da",
										minWidth: "220px",
										paddingRight: "40px",
									}}
								/>
								{searchInput && (
									<span
										onClick={() => removeParam()}
										style={{
											position: "absolute",
											right: "15px",
											top: "50%",
											transform: "translateY(-50%)",
											cursor: "pointer",
											fontSize: "18px",
											color: "#888",
										}}
									>
										&times;
									</span>
								)}
							</div>
							<div style={{ position: "relative" }}>
								<Input
									type="select"
									value={sortOrder}
									onChange={(e) => {
										setSortOrder(e.target.value);
										setPageNo(1);
										fetchBlogs(1, searchTerm, e.target.value);
									}}
									className="form-control rounded-pill shadow-sm px-3"
									style={{
										height: "45px",
										borderColor: "#ced4da",
										minWidth: "170px",
										cursor: "pointer",
									}}
								>
									<option value="Latest">Sort by: Latest</option>
									<option value="Oldest">Sort by: Oldest</option>
								</Input>
							</div>
						</div>
					</div>
				</div>
				{loading ? (
					<Row>
						{Array.from({ length: pageSize }).map((_, i) => (
							<Col lg="4" md="6" sm="12" key={i} className="mb-4">
								<Skeleton height={250} />
								<Skeleton
									height={20}
									width="80%"
									style={{ marginTop: 10 }}
								/>
								<Skeleton height={15} width="60%" />
								<Skeleton height={40} />
							</Col>
						))}
					</Row>
				) : blogs.length > 0 ? (
					<Row>
						{blogs
							.slice((pageNo - 1) * pageSize, pageNo * pageSize)
							.map((blog, index) => (
								<Col lg="4" md="6" sm="12" key={index} className="mb-4 d-flex">
									<div className="learn-card shadow-sm border-0 bg-white w-100">
										<div className="learncard-img-wrapper">
											<ImageWithSkeleton
												src={blog.IMAGEPATH && blog.IMAGEPATH !== "null" ? blog.IMAGEPATH : blog.IMAGEURL && blog.IMAGEURL !== "null" ? blog.IMAGEURL : fallbackImg}
												alt={blog.BLOGTITLE}
												height={250}
												className="learncard-img-top"
											/>
										</div>
										<div className="card-content-wrapper d-flex flex-column p-3">
											<h6 className="learn-card-title">{blog.BLOGTITLE}</h6>
											<p className="learn-card-date">
												{new Date(blog.POSTDATE).toLocaleDateString()}
											</p>
											<p className="learn-card-text">
												{blog.BLOGDESCRIPTION?.slice(0, 100)}...
											</p>

											<Link
												to={`/learn/blog/${blog.SLUG}`}
												className="read-more"
												onClick={() => handleReadMore(blog.SLUG)}
											>
												<span className="text">Read More</span>
												<span className="icon">
													<i className="bx bx-right-arrow-alt"></i>
												</span>
											</Link>
										</div>
									</div>
								</Col>
							))}
					</Row>
				) : (
					//<div className="text-center text-muted py-5">
					//	{selectedBlog && (
					//		<div className="blog-detail mt-5 p-4 shadow-sm bg-white rounded">
					//			{detailLoading ? (
					//				<p>Loading blog detail...</p>
					//			) : (
					//				<>
					//					<h2>{selectedBlog.BLOGTITLE}</h2>
					//					<p>{new Date(selectedBlog.POSTDATE).toLocaleDateString()}</p>
					//					{selectedBlog.IMAGEPATH && (
					//						<img
					//							src={selectedBlog.IMAGEPATH}
					//							alt={selectedBlog.BLOGTITLE}
					//							style={{ width: "100%", maxHeight: "400px", objectFit: "contain" }}
					//							onError={(e) => { e.target.src = fallbackImg; }}
					//						/>
					//					)}
					//					<div className="blog-content mt-3" dangerouslySetInnerHTML={{ __html: selectedBlog.BLOGCONTENT }} />
					//					<button className="btn btn-secondary mt-3" onClick={() => setSelectedBlog(null)}>
					//						Close
					//					</button>
					//				</>
					//			)}
					//		</div>
					//	)}
					//</div>
					<div className="empty-blog-wrapper">
						<div className="empty-blog-state text-center">
							<img
								src="/assets/images/blog/noBlog.png"
								alt="No blogs found"
								className="empty-blog-image"
							/>

							<h5 className="mt-3 mb-2">No Blogs Found</h5>

							<p className="text-muted mb-0">
								{error}
							</p>
						</div>
					</div>
				)}
				{totalPages > 1 && (
					<div className="d-flex justify-content-center mt-4">
						<Pagination aria-label="Page navigation" className="custom-pagination">

							{/* Previous Button */}
							<PaginationItem disabled={pageNo === 1}>
								<PaginationLink
									previous
									href="#"
									onClick={(e) => {
										e.preventDefault();
										handlePageChange(pageNo - 1);
									}}
								/>
							</PaginationItem>
							{(() => {
								const pages = [];
								const maxVisible = 5;
								let start = Math.max(1, pageNo - Math.floor(maxVisible / 2));
								let end = Math.min(totalPages, start + maxVisible - 1);

								if (end - start + 1 < maxVisible) {
									start = Math.max(1, end - maxVisible + 1);
								}

								if (start > 1) {
									pages.push(
										<PaginationItem key={1} active={pageNo === 1}>
											<PaginationLink
												href="#"
												onClick={(e) => {
													e.preventDefault();
													handlePageChange(1);
												}}
											>
												1
											</PaginationLink>
										</PaginationItem>
									);
									if (start > 2) {
										pages.push(
											<PaginationItem key="dots-start" disabled>
												<PaginationLink href="#">...</PaginationLink>
											</PaginationItem>
										);
									}
								}
								for (let i = start; i <= end; i++) {
									pages.push(
										<PaginationItem key={i} active={i === pageNo}>
											<PaginationLink
												href="#"
												onClick={(e) => {
													e.preventDefault();
													handlePageChange(i);
												}}
											>
												{i}
											</PaginationLink>
										</PaginationItem>
									);
								}
								if (end < totalPages) {
									if (end < totalPages - 1) {
										pages.push(
											<PaginationItem key="dots-end" disabled>
												<PaginationLink href="#">...</PaginationLink>
											</PaginationItem>
										);
									}
									pages.push(
										<PaginationItem key={totalPages} active={pageNo === totalPages}>
											<PaginationLink
												href="#"
												onClick={(e) => {
													e.preventDefault();
													handlePageChange(totalPages);
												}}
											>
												{totalPages}
											</PaginationLink>
										</PaginationItem>
									);
								}

								return pages;
							})()}
							<PaginationItem disabled={pageNo === totalPages}>
								<PaginationLink
									next
									href="#"
									onClick={(e) => {
										e.preventDefault();
										handlePageChange(pageNo + 1);
									}}
								/>
							</PaginationItem>
						</Pagination>
					</div>
				)}
			</div>
		</section>
	);
};

export default BlogsSection;
